import SimpleMonacoEditor from "@/components/CodeEditors/SimpleMonacoEditor";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { Message } from "@/store/zustand/supabaseStore";
import { getEditorLanguageForFilename, useCanvasId } from "@/utils/utils";
import {
  CheckIcon,
  CircleAlertIcon,
  ClockIcon,
  HistoryIcon,
  PlayIcon,
  RefreshCcwIcon,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { ChatAiCodeChanges } from "../ChatAiCodeChanges";
import { useApplyChatBlock } from "../useApplyChatBlock";
import { MessageSegment } from "../utils/messages";
import { useDispatch, useSelector } from "react-redux";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import useActivityStore from "@/store/zustand/activityStore";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { useSession } from "@clerk/clerk-react";

export interface CommitProps {
  message: Message;

  block: MessageSegment;
  blockIndex: number;

  loadingAiChange: boolean;
}

export const EngSpecBlock = React.memo(
  ({ message, block, blockIndex, loadingAiChange }: CommitProps) => {
    const [contextExpanded, setContextExpanded] = useState(false);

    const dispatch = useDispatch();

    const activityStreamList = useSelector(
      (state: any) => state.supabaseStore.activityStreamList
    );

    const appliedBlock = message.message_context?.applied_segment_indices?.find(
      (idx: number) => idx === blockIndex
    );

    const restoreActivity = useActivityStore((state) => state.restoreActivity);

    const { triggerRefetchActivityStream } = useSupabase();

    const { savedActivity, savedActivityIsTerminal, savedActivityIsCurrent } =
      useMemo(() => {
        const savedActivity =
          message.message_context?.savedActivityId === "RAN_COMMAND" ||
          activityStreamList?.find?.(
            (activity) =>
              message.message_context?.savedActivityId === activity.id
          );
        const savedActivityIsTerminal =
          message.message_context?.savedActivityId === "RAN_COMMAND";

        const currentActivity = activityStreamList?.find?.(
          (activity) => !activity.change_undone
        );

        const savedActivityIsCurrent =
          !savedActivityIsTerminal && currentActivity === savedActivity;

        return {
          savedActivity,
          savedActivityIsTerminal,
          savedActivityIsCurrent,
        };
      }, [message.message_context?.savedActivityId, activityStreamList]);

    const canvasId = useCanvasId();

    const aiChatEngSpecMessageId = useAiContextStore(
      (state) => state.aiChatEngSpecMessageId
    );
    const aiChatEngSpecBlockIndex = useAiContextStore(
      (state) => state.aiChatEngSpecBlockIndex
    );

    const leftTab = useLayoutStore((state) => state.leftTab);
    const setNewLayout = useLayoutStore((state) => state.setNewLayout);

    const openAiChatEngSpec = useAiContextStore(
      (state) => state.openAiChatEngSpec
    );
    const closeAiChatEngSpec = useAiContextStore(
      (state) => state.closeAiChatEngSpec
    );

    const { applyChatBlock, canApplyChatBlock } = useApplyChatBlock();

    const { session } = useSession();

    const showOpenButton =
      message.role !== "user" && block.type === "aiChanges";

    const aiChangesOpened =
      showOpenButton &&
      aiChatEngSpecMessageId === message.id &&
      aiChatEngSpecBlockIndex === blockIndex &&
      leftTab === LeftTab.EngSpec;

    const showViewCloseButton =
      message.role === "user" &&
      (message.message_context?.commit ||
        message.message_context?.file ||
        message.message_context?.plan ||
        message.message_context?.codeSnippet) &&
      !message.message_context?.makeNew;

    const canApplyBlock = useMemo(() => {
      // If it was applied before means it was allowed
      if (appliedBlock) return true;

      return canApplyChatBlock(message, block);
    }, [appliedBlock, message, block]);

    const showApplyButton = message.role !== "user" && canApplyBlock; //canApplySegments[index];

    let codeSnippetFilename, codeSnippetStartLine, codeSnippetEndLine;
    if (block.type === "aiCodeChange" || block.type === "codeSnippet") {
      codeSnippetFilename =
        block.type === "aiCodeChange"
          ? block.aiCodeChange?.filename
          : block.codeSnippet?.filename;
      codeSnippetStartLine =
        block.type === "aiCodeChange"
          ? block.aiCodeChange.edit?.startLine
          : block.codeSnippet?.startLine;
      codeSnippetEndLine =
        block.type === "aiCodeChange"
          ? block.aiCodeChange.edit?.endLine
          : block.codeSnippet?.endLine;
      if (codeSnippetFilename?.includes?.(canvasId)) {
        codeSnippetFilename = codeSnippetFilename.split(canvasId + "/")[1];
      }
    }

    const restoreDisabled =
      savedActivityIsTerminal || savedActivityIsCurrent || !savedActivity;
    let icon, text;
    if (savedActivityIsTerminal) {
      icon = <HistoryIcon className="w-2.5 h-2.5" />;
      text = "Terminal";
    } else if (savedActivityIsCurrent) {
      icon = <HistoryIcon className="w-2.5 h-2.5" />;
      text = "Current";
    } else if (!savedActivity) {
      icon = <CircleAlertIcon className="w-2.5 h-2.5" />;
      text = "Overwritten";
    } else {
      icon = <RefreshCcwIcon className="w-2.5 h-2.5" />;
      text = "Restore";
    }

    return (
      <div
        className={cn(
          "flex flex-col grow bg-secondary-200 px-1 rounded-md",
          !contextExpanded && showViewCloseButton && "pb-1"
        )}
      >
        <div className="flex flex-row justify-between w-full items-center mt-1 px-0.5">
          <div
            className={
              "bg-secondary-500 text-secondary-800 rounded-md text-2xs px-1.5 h-5 flex items-center grow-0 w-auto"
            }
          >
            {block.type === "aiCodeChange" || block.type === "codeSnippet"
              ? `Code: ${codeSnippetFilename}:${codeSnippetStartLine}-${codeSnippetEndLine}`
              : "Change"}
          </div>
          <div className="flex flex-row gap-x-1">
            <Button
              size="xs"
              variant="outline"
              disabled={restoreDisabled}
              className={cn(
                "text-2xs h-6 px-1.5 gap-1",
                loadingAiChange && "hidden",
                !message.message_context?.savedActivityId && "hidden"
              )}
              onClick={() => {
                if (!session) return;

                setNewLayout(LeftTab.History, undefined);
                restoreActivity(
                  dispatch,
                  canvasId,
                  savedActivity.id,
                  activityStreamList,
                  triggerRefetchActivityStream,
                  session
                );
              }}
            >
              {icon}
              <span>{text}</span>
            </Button>
            <Button
              size="xs"
              variant="outline"
              className={cn(
                "text-2xs h-6 px-1.5 gap-1",
                !showViewCloseButton &&
                  !showApplyButton &&
                  !showOpenButton &&
                  "hidden",
                appliedBlock &&
                  !showViewCloseButton &&
                  !showOpenButton &&
                  "cursor-default",
                block.jsonIsBroken && "hidden"
              )}
              onClick={async () => {
                if (showOpenButton) {
                  if (!aiChangesOpened) {
                    // @ts-expect-error TS2345
                    setNewLayout(LeftTab.EngSpec, null);
                  }

                  if (aiChangesOpened) {
                    closeAiChatEngSpec();
                  } else {
                    openAiChatEngSpec(blockIndex, message.id);
                  }

                  return;
                }

                if (showViewCloseButton) {
                  setContextExpanded(!contextExpanded);
                  return;
                }

                if (appliedBlock) {
                  return;
                }

                applyChatBlock(message, block, blockIndex);
              }}
            >
              {showOpenButton ? (
                aiChangesOpened ? (
                  "Close"
                ) : (
                  "Open"
                )
              ) : showViewCloseButton ? (
                contextExpanded ? (
                  "Close"
                ) : (
                  "View"
                )
              ) : appliedBlock ? (
                <>
                  <CheckIcon className="w-2 h-2" />
                  Applied
                </>
              ) : (
                <>
                  <PlayIcon className="w-2 h-2" />
                  Apply
                </>
              )}
            </Button>
          </div>
        </div>
        <div
          className={cn(
            !contextExpanded &&
              showViewCloseButton &&
              !showOpenButton &&
              "hidden"
          )}
        >
          {block.jsonIsBroken ? (
            <div className="text-xs p-1.5">
              Change provided by the AI is not valid JSON. Please try again.
            </div>
          ) : block.type === "aiChanges" ? (
            <ChatAiCodeChanges
              changes={block.aiChanges?.changes}
              loadingAiChange={loadingAiChange}
              messageContext={message.message_context}
            />
          ) : block.type === "codeSnippet" || block.type === "aiCodeChange" ? (
            <SimpleMonacoEditor
              theme={"tempo-theme"}
              value={
                block.type === "aiCodeChange"
                  ? block.aiCodeChange?.edit?.newContent?.join?.("\n")
                  : block.codeSnippet?.contents
              }
              additionalOptions={{
                readOnly: true,
                lineNumbers: (num) =>
                  (
                    (block.type === "aiCodeChange"
                      ? block.aiCodeChange?.edit?.startLine || 0
                      : block.codeSnippet.startLine) +
                    num -
                    1
                  ).toString(),
              }}
              language={getEditorLanguageForFilename(
                block.type === "aiCodeChange"
                  ? block.aiCodeChange?.filename
                  : block.codeSnippet.filename
              )}
              originalValue={
                block.type === "codeSnippet"
                  ? block?.diffCodeSnippet?.contents
                  : undefined
              }
              diff={
                block?.diffCodeSnippet?.contents ||
                block?.diffCodeSnippet?.contents === ""
              }
            />
          ) : null}
        </div>
      </div>
    );
  }
);
